import { Link } from "react-router-dom";
import { Target, Eye, Gem, ArrowRight } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { PROCESS, SERVICES } from "../data/site";

const VALUES = [
  ["Integrity", "Transparent pricing and honest reporting on every order."],
  ["Precision", "Engineering-grade attention to specs, tolerances and quality."],
  ["Partnership", "We win when you win — long-term relationships over transactions."],
  ["Speed", "Local presence means faster samples, decisions and resolutions."],
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader
        overline="About Us"
        title="On the ground in India."
        subtitle="SourceX Global gives international buyers a trusted, transparent presence inside India's manufacturing ecosystem."
      />

      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-6">
          <Reveal>
            <div className="relative bg-navy text-white aspect-[4/3] p-10 flex flex-col justify-between overflow-hidden">
              <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-brand-orange/25 blur-3xl" />
              <div className="absolute inset-0 grain opacity-[0.06]" />
              <p className="overline relative">Sourcing From India</p>
              <div className="grid grid-cols-2 gap-8 relative">
                {[["500+", "Verified Suppliers"], ["45+", "Commodities"], ["18", "Industries"], ["15+", "Export Markets"]].map(([n, l]) => (
                  <div key={l}>
                    <div className="font-display font-black text-4xl text-brand-orange">{n}</div>
                    <div className="font-mono uppercase text-[10px] tracking-[0.15em] text-white/60 mt-1">{l}</div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
        <div className="md:col-span-6">
          <Reveal><p className="overline mb-4">Company Overview</p></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-black text-3xl md:text-4xl mb-6 tracking-tight">Bridging global demand and Indian capability.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-muted-foreground leading-relaxed text-lg mb-5">
              SourceX Global is a full-service sourcing and procurement partner headquartered in India,
              built to help international buyers tap into one of the world's largest and most
              cost-competitive manufacturing bases with total confidence. We act as your dedicated
              team on the ground — closing the distance, quality and compliance gaps that make
              overseas sourcing risky.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-muted-foreground leading-relaxed">
              Importers, OEMs, distributors, wholesalers and startups rely on us to identify and audit
              vendors, manage RFQs, optimise cost, develop samples, monitor production, inspect quality
              and coordinate export logistics — across 45+ engineering commodities and 18 industries.
            </p>
          </Reveal>
        </div>
      </section>

      {/* MISSION / VISION */}
      <section className="bg-secondary py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-px bg-border border border-border">
          {[
            { icon: Target, t: "Mission", d: "To make sourcing from India effortless, transparent and reliable for global buyers — de-risking supply chains through hands-on local execution." },
            { icon: Eye, t: "Vision", d: "To be the most trusted sourcing bridge between international markets and Indian manufacturing excellence." },
          ].map((b, i) => (
            <Reveal key={b.t} delay={i * 0.1} className="bg-card p-10 md:p-14">
              <b.icon size={34} className="text-brand-orange mb-6" strokeWidth={1.5} />
              <h3 className="font-display font-black text-2xl mb-4">{b.t}</h3>
              <p className="text-muted-foreground leading-relaxed">{b.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="overline mb-4">Our Values</p></Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display font-black text-4xl md:text-5xl mb-14 tracking-tight flex items-center gap-4">
            <Gem className="text-brand-orange" size={38} /> What we stand for.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
          {VALUES.map(([t, d], i) => (
            <Reveal key={t} delay={i * 0.05} className="bg-card p-8">
              <div className="font-mono text-brand-orange text-sm mb-4">0{i + 1}</div>
              <h3 className="font-display font-bold text-xl mb-2">{t}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="bg-navy text-white py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-5 sm:px-8">
          <Reveal><p className="overline mb-4">Our Services</p></Reveal>
          <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-14">End-to-end sourcing.</h2></Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-white/10 border border-white/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={(i % 5) * 0.05} className="bg-navy p-6">
                <s.icon size={24} className="text-brand-orange mb-4" strokeWidth={1.6} />
                <h3 className="font-display font-bold text-sm">{s.title}</h3>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-[1440px] mx-auto px-5 sm:px-8 py-24 md:py-32">
        <Reveal><p className="overline mb-4">Our Sourcing Process</p></Reveal>
        <Reveal delay={0.1}><h2 className="font-display font-black text-4xl md:text-6xl tracking-tight mb-16 max-w-2xl">How we deliver.</h2></Reveal>
        <div className="relative border-l-2 border-border ml-3">
          {PROCESS.map((p, i) => (
            <Reveal key={p.n} delay={(i % 4) * 0.05} className="relative pl-10 pb-10 last:pb-0">
              <span className="absolute -left-[11px] top-0 h-5 w-5 bg-brand-orange grid place-items-center"><span className="h-2 w-2 bg-white" /></span>
              <div className="font-mono text-brand-orange text-sm mb-1">{p.n}</div>
              <h3 className="font-display font-bold text-xl mb-1">{p.title}</h3>
              <p className="text-muted-foreground leading-relaxed max-w-xl">{p.desc}</p>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <Link to="/quote" className="mt-12 inline-flex items-center gap-2 bg-brand-orange text-white px-8 py-4 font-mono uppercase text-[11px] tracking-[0.18em] hover:bg-brand-orangeHover transition-colors">
            Start Your Enquiry <ArrowRight size={15} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
